import { useMemo, useState } from "react";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { PushToSftpTableDataModel } from "../../../../data-model/pushToSftpDataModel";
import { PushToSftpModalModes } from "../../../../enums/StringLiterals/pushToSftpModalModes";
import { TableNames } from "../../../../enums/tableNames";
import CustomTable from "../../../custom-table/custom-table";
import { useDownloadSftpFile } from "../hooks/useDownloadSftpFile";
import { useTableDataForSftpPush } from "../hooks/useTableDataForSftpPush";
import { generatePushToSftpTableData } from "../utils/pushToSftpTableDataGenerator";
import { toggleCheckboxSelection } from "../utils/toggleCheckBoxSelection";
import {
  areAllPageItemsSelected,
  toggleSelectAllOnPage,
} from "../utils/toggleSelectAllOnPage";

export interface PushToSftpTableState {
  tableData: PushToSftpTableDataModel[];
  setTableData: React.Dispatch<
    React.SetStateAction<PushToSftpTableDataModel[]>
  >;
  isLoading: boolean;
  showInlineLoader: boolean;
  totalCount: number;
  setEndIndex: (index: number) => void;
}

interface PushToSftpTableContentProps {
  mode?: string;
  showDownloadColumn?: boolean;
  /** When provided, table uses this state instead of internal hook (controlled mode) */
  tableState?: PushToSftpTableState;
}

const PushToSftpTableContent = ({
  mode = PushToSftpModalModes.TRACK,
  showDownloadColumn = false,
  tableState: controlledState,
}: PushToSftpTableContentProps) => {
  const [currentStartIndex, setCurrentStartIndex] = useState(0);
  const [currentEndIndex, setCurrentEndIndex] = useState(9);

  const internalState = useTableDataForSftpPush(mode);

  const {
    tableData,
    setTableData,
    isLoading,
    showInlineLoader,
    totalCount,
    setEndIndex,
  } = controlledState ?? internalState;

  const { downloadFile } = useDownloadSftpFile();

  const handleSelectAll = () => {
    toggleSelectAllOnPage(currentStartIndex, currentEndIndex, setTableData);
  };

  const isAllPageItemsSelected = useMemo(
    () =>
      areAllPageItemsSelected(tableData, currentStartIndex, currentEndIndex),
    [tableData, currentStartIndex, currentEndIndex],
  );

  const hideCheckboxColumn = mode === PushToSftpModalModes.PUSHED_DOWNLOAD;

  if (isLoading && tableData.length === 0) {
    return (
      <div className="flex h-full items-center justify-center gap-2">
        <SpinningLoader />
        <span>Loading...</span>
      </div>
    );
  }

  return (
    <div className="sftp-modal-container--table h-full w-full overflow-y-auto">
      <CustomTable
        tableHeaders={generatePushToSftpTableData(
          showDownloadColumn,
          hideCheckboxColumn,
        )}
        tableData={tableData}
        tableName={TableNames.PUSH_TO_SFTP_MODAL_TABLE}
        itemsPerPage={10}
        pagesPerView={4}
        hideSearchBar={true}
        count={totalCount}
        totalCount={totalCount}
        toggleCheckboxSelection={
          hideCheckboxColumn
            ? undefined
            : (id) => toggleCheckboxSelection(id, setTableData)
        }
        hideSelectAllCheckbox={hideCheckboxColumn}
        onSelectAll={hideCheckboxColumn ? undefined : handleSelectAll}
        isCheckboxSelected={hideCheckboxColumn ? false : isAllPageItemsSelected}
        startIndexOfTable={setCurrentStartIndex}
        endIndexOfTable={(endIdx) => {
          setCurrentEndIndex(endIdx);
          setEndIndex(endIdx);
        }}
        showInLineLoader={showInlineLoader}
        onDownloadIconClick={downloadFile}
      />
    </div>
  );
};

export default PushToSftpTableContent;
